import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { productApi, stripeApi } from '../services/api';
import { Lock, Check, Loader2, Plane, AlertCircle, LogOut, Sparkles } from 'lucide-react';

const NoEditionPage = () => {
  const { user, logout } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Dynamic pricing
  const [stripePrice, setStripePrice] = useState(null);

  useEffect(() => {
    fetchProduct();
    fetchStripePrice();
  }, []);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const { data } = await productApi.get('core');
      setProduct(data);
    } catch (err) {
      console.error('Failed to fetch product:', err);
      setError('Failed to load product information');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripePrice = async () => {
    try {
      const { data } = await stripeApi.getPrice();
      setStripePrice(data);
    } catch (err) {
      console.error('Failed to fetch Stripe price:', err);
    }
  };

  const handlePurchase = async () => {
    try {
      setCheckoutLoading(true);
      setError('');
      
      const originUrl = window.location.origin;
      // Backend fetches price directly from Stripe
      const { data } = await stripeApi.createCheckoutSession('core', originUrl);
      
      // Log redirect
      console.log('Redirecting to Stripe:', data.url);
      
      // Full page redirect - handle iframe case
      if (window.top !== window.self) {
        // Inside iframe - redirect parent window
        window.top.location.href = data.url;
      } else {
        // Normal case - direct redirect
        window.location.href = data.url;
      }
      return;
    } catch (err) {
      console.error('Checkout error:', err);
      const message = err.response?.data?.detail || 'Failed to start checkout';
      setError(message);
      setCheckoutLoading(false);
    }
  };

  // Static features for consistent display
  const features = [
    "Structured cockpit procedures",
    "Lighting configuration guidance",
    "ATC communication examples",
    "Optional add-on integration notes",
    "Operational insights explaining each phase"
  ];

  // Dynamic price display - Stripe is source of truth
  const displayPrice = stripePrice 
    ? `${stripePrice.currency === 'GBP' ? '£' : stripePrice.currency + ' '}${stripePrice.price.toFixed(2)}`
    : null;  // No fallback - wait for Stripe price

  return (
    <div className="min-h-screen bg-cockpit-bg flex items-center justify-center p-4" data-testid="no-edition-page">
      <div className="w-full max-w-2xl">
        {/* Premium Card */}
        <div className="bg-gradient-to-b from-cockpit-panel to-cockpit-bg border border-cockpit-border rounded-sm shadow-card overflow-hidden">
          {/* Decorative top accent */}
          <div className="h-1 bg-gradient-to-r from-airbus-blue via-fsa-blue to-airbus-blue" />
          
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-airbus-blue/10 rounded-full mb-6 border border-airbus-blue/20">
              <Plane className="w-10 h-10 text-airbus-blue" />
            </div>
            
            <div className="space-y-2">
              <p className="text-fsa-gold text-sm font-medium tracking-wider uppercase">
                Flight Sim Academy
              </p>
              <h1 className="text-2xl md:text-3xl font-semibold text-cockpit-text-primary">
                Fenix A320 Edition
              </h1>
            </div>
            
            <p className="text-cockpit-text-secondary mt-4">
              Get lifetime access to the complete A320 procedural flow system
            </p>
          </div>

          {/* Content */}
          <div className="px-8 pb-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-airbus-blue animate-spin" />
              </div>
            ) : (
              <>
                {/* Description */}
                <div className="text-center mb-8">
                  <p className="text-cockpit-text-secondary leading-relaxed">
                    Unlock the complete Airbus A320 procedural flight flow system.
                  </p>
                  <p className="text-cockpit-text-secondary leading-relaxed mt-2">
                    Includes every structured phase from Cold & Dark through to Shutdown and Securing the Aircraft.
                  </p>
                </div>

                {/* Features Grid */}
                <div className="bg-cockpit-bg/50 border border-cockpit-border rounded-sm p-6 mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-fsa-gold" />
                    <h3 className="text-sm font-medium text-cockpit-text-primary uppercase tracking-wide">
                      What's Included
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-status-success flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-cockpit-text-primary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price Section */}
                <div className="bg-cockpit-panel border border-cockpit-border rounded-sm p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-cockpit-text-secondary uppercase tracking-wide mb-1">
                        One-time payment
                      </p>
                      <p className="text-sm text-cockpit-text-secondary">
                        Lifetime access included
                      </p>
                    </div>
                    <div className="text-right">
                      {displayPrice ? (
                        <span className="text-4xl font-bold text-cockpit-text-primary">
                          {displayPrice}
                        </span>
                      ) : (
                        <Loader2 className="w-8 h-8 text-airbus-blue animate-spin" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded-sm flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                {/* CTA Button */}
                <button
                  onClick={handlePurchase}
                  disabled={checkoutLoading}
                  className="w-full bg-airbus-blue hover:bg-airbus-hover text-white font-semibold py-4 px-6 rounded-sm transition-all duration-200 flex items-center justify-center gap-3 text-base shadow-glow hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                  data-testid="unlock-workflow-btn"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Redirecting to checkout...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Unlock Full Workflow
                    </>
                  )}
                </button>

                <p className="text-xs text-cockpit-text-secondary text-center mb-6">
                  Secure payment powered by Stripe
                </p>
              </>
            )}

            {/* User info and logout */}
            <div className="border-t border-cockpit-border pt-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-cockpit-text-secondary">
                  Logged in as: <strong className="text-cockpit-text-primary">{user?.email}</strong>
                </p>
                <button
                  onClick={logout}
                  className="btn-secondary flex items-center gap-2 text-sm"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoEditionPage;
